import { Check, CheckCircle2, Clock, Minus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { Quote } from '@/types';
import {
  describeSignatureOverview,
  describeSigner,
  describeUboSigner,
  getClientSignatureLabel,
  getSignatureOverview,
  getUboSignatureLabel,
  type QuoteSignatureOverview,
} from './signatureStatus';

/**
 * De ondertekenstatus zoals het overzicht hem toont: één chip voor de klant en
 * één voor UBO, en zodra iedereen klaar is één chip die dat samenvat. De namen
 * van de ondertekenaars staan in de tooltip — in een lijstrij of op een
 * pipelinekaart is daar geen ruimte voor, maar de vraag 'wie heeft getekend'
 * moet wel zonder de offerte te openen te beantwoorden zijn.
 *
 * `compact` gebruikt kortere teksten voor de kaart- en pipelineweergave, waar de
 * chips naast de status op één regel moeten passen.
 */

function SignatureChip({
  label,
  state,
  className,
}: {
  label: string;
  state: 'signed' | 'pending' | 'open';
  className?: string;
}) {
  const Icon = state === 'signed' ? Check : state === 'pending' ? Clock : Minus;
  return (
    <Badge
      variant={state === 'signed' ? 'success' : state === 'pending' ? 'warning' : 'outline'}
      className={cn(
        'gap-1 whitespace-nowrap font-medium',
        state === 'open' && 'border-dashed text-muted-foreground',
        className,
      )}
    >
      <Icon className="h-3 w-3 shrink-0" />
      {label}
    </Badge>
  );
}

function SignatureTooltip({ overview }: { overview: QuoteSignatureOverview }) {
  return (
    <div className="max-w-[260px] space-y-2 text-xs leading-snug">
      <div>
        <p className="font-semibold">Klant</p>
        {overview.clientSigners.length === 0 ? (
          <p className="opacity-80">Geen tekenbevoegden ingesteld</p>
        ) : (
          <ul className="space-y-0.5">
            {overview.clientSigners.map((signer, index) => (
              <li key={`${signer.name}-${index}`} className={cn(!signer.signed && 'opacity-80')}>
                {describeSigner(signer)}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <p className="font-semibold">UBO</p>
        <p className={cn(!overview.uboSignedAt && 'opacity-80')}>{describeUboSigner(overview)}</p>
      </div>
      <p className="border-t border-primary-foreground/25 pt-1.5 opacity-90">
        {overview.everyoneSigned ? 'Door iedereen ondertekend.' : 'Nog niet door iedereen ondertekend.'}
      </p>
    </div>
  );
}

export function QuoteSignatureStatus({
  quote,
  compact = false,
  className,
}: {
  quote: Quote;
  compact?: boolean;
  className?: string;
}) {
  const overview = getSignatureOverview(quote);
  if (!overview.relevant) return null;

  const summaryText = describeSignatureOverview(overview);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn('flex flex-wrap items-center gap-1', className)}
          aria-label={summaryText}
          title={summaryText}
        >
          {overview.everyoneSigned ? (
            <Badge variant="success" className="gap-1 whitespace-nowrap font-medium">
              <CheckCircle2 className="h-3 w-3 shrink-0" />
              {compact ? 'Volledig' : 'Volledig ondertekend'}
            </Badge>
          ) : (
            <>
              <SignatureChip
                label={getClientSignatureLabel(overview, compact)}
                state={
                  overview.client === 'signed' ? 'signed' : overview.client === 'partial' ? 'pending' : 'open'
                }
              />
              <SignatureChip
                label={getUboSignatureLabel(overview, compact)}
                state={overview.ubo === 'signed' ? 'signed' : overview.awaitingUbo ? 'pending' : 'open'}
              />
            </>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" align="start" className="px-3 py-2">
        <SignatureTooltip overview={overview} />
      </TooltipContent>
    </Tooltip>
  );
}

export default QuoteSignatureStatus;
